import { CircleAlert } from 'lucide-react';

import { setOrganizationFullAddress } from 'src/utils/escrow';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { Card, CardTitle, CardHeader, CardContent, CardDescription, CardFooter } from '@/components/ui/card';
import { OrganizationRegistrationNumberTooltip } from '@/components/Organization';

import { EditBeneficiaryDialog, EmptyBeneficiary } from '.';

import { useBeneficiaryContext } from '../model';

const CardWrapper = ({
  children,
  isRequired = false,
  footer,
}: {
  children: React.ReactNode;
  isRequired?: boolean;
  footer?: React.ReactNode;
}) => {
  return (
    <Card className="flex flex-col">
      <CardHeader className="shrink-0">
        <CardTitle className="flex items-center gap-2">
          Beneficiary organization details
          {isRequired && (
            <span className="text-orange-700 flex items-center gap-1">
              <CircleAlert className="size-3.5" />
              <i className="text-xs font-medium">Required</i>
            </span>
          )}
        </CardTitle>
        <CardDescription>
          What are the beneficiary company&apos;s details?
          <br />
          This includes the registered name and address of the entity, which will be listed in the escrow agreement.
        </CardDescription>
      </CardHeader>
      <CardContent className="grow">{children}</CardContent>
      {footer}
    </Card>
  );
};

const BeneficiaryOrganization = () => {
  const { beneficiary } = useBeneficiaryContext();

  if (!beneficiary)
    return (
      <CardWrapper isRequired>
        <EmptyBeneficiary />
      </CardWrapper>
    );

  const address = {
    street: beneficiary?.street || '',
    streetNumber: beneficiary?.streetNumber || '',
    city: beneficiary?.city || '',
    postalCode: beneficiary?.postalCode || '',
    region: beneficiary?.region || '',
    country: beneficiary?.country || '',
  };

  const rows = [
    {
      id: 'organization-name',
      label: 'Organization Name',
      value: beneficiary?.companyName,
    },
    {
      id: 'website',
      label: 'Website',
      value: beneficiary?.companyWebsite || '-',
    },
    {
      id: 'registration-number',
      label: (
        <div className="flex items-center gap-1">
          Registration Number <OrganizationRegistrationNumberTooltip />
        </div>
      ),
      value: beneficiary?.companyRegistrationNumber || '-',
    },
    {
      id: 'address',
      label: 'Address',
      value: setOrganizationFullAddress(address),
    },
  ];

  return (
    <CardWrapper
      footer={
        beneficiary?.policy.update && (
          <CardFooter className="pt-6 shrink-0">
            <div className="flex justify-end bg-muted w-full py-2 px-4">
              <EditBeneficiaryDialog />
            </div>
          </CardFooter>
        )
      }
    >
      <Table>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="font-semibold w-[190px] text-muted-foreground">{row.label}</TableCell>
              <TableCell className="w-[85px]">:</TableCell>
              <TableCell>
                <div className="min-w-[190px]">{row.value}</div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardWrapper>
  );
};

export default BeneficiaryOrganization;
